import {React, observe, _, action, useLocalStore, computedFn, useContext} from '@api'
import {WorkspaceChooserStore} from '@design-board'
import {DeploymentConfig, deployments, nav, WorkspaceConfig, WorkspaceId, Workspace, LibraryItemConfigWrapper, LibraryItemConfigWrapperStore, ICatalogFieldConfig, DeploymentPdfConfig, globalStores, CatalogStore, StoresInstance, CatalogImageSourceConfig, DEBUG_WORKSPACES, WorkspaceChangeLogConfig, DEFAULT_CHANGE_LOG_CONFIG, ICatalogUserSettings} from '@stores'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {computed, observable, autorun, toJS, runInAction} from 'mobx'
import {IDisposer} from 'mobx-utils'

export type StoresByWorkspace = { [workspaceId: string]: StoresByWorkspace }

export class WorkspacesStore {
  chooserStore: WorkspaceChooserStore

  _toDispose: IDisposer[] = []
  @action dispose = () => {
    this._toDispose.forEach(d => d())
  }

  constructor(public catalog: CatalogStore) {
    synchronizeStores(() => {
      const {_toDispose} = this
      if (catalog.isGlobalStore) {
        _toDispose.push(
          autorun(() => {
            const {deploymentConfig} = this
            if (!deploymentConfig) return

            const workspaces = deploymentConfig?.workspaces ?? []

            //DEBUG && trace()
            runInAction(() => {
              DEBUG_WORKSPACES && console.groupCollapsed(`Loading workspaces for deployment ${deploymentConfig.title} - ${globalStores.config.config.apiGatewayEndpoint}`, toJS(deploymentConfig))
              this.workspaces.forEach(ws => ws.dispose())
              this.workspaces = workspaces.map(ws => new Workspace(this, ws, deploymentConfig))
              DEBUG_WORKSPACES && console.groupEnd()
            })
          }, {name: `Wrap current workspace with store`}))
      } else {
        _toDispose.push(
          autorun(() => {
            this.workspaces = globalStores.workspaces.workspaces
            this.current = catalog.stores._workspace?._id ?? _.first(this.availableWorkspaces)?._id
          }))
      }
    })
  }

  @computed get firstAvailableWorkspace() {
    return _.first(this.availableWorkspaces)
  }

  @computed get fieldByWorkspaceIndex(): { [index: string]: { workspace: Workspace, fields: { [field: string]: ICatalogFieldConfig } } } {
    // trace()
    const {availableWorkspaces, wrapperById} = this
    return availableWorkspaces
      .filter(ws => ws?.catalog?.fields)
      .reduce((acc, ws) => _.assign(acc, {
        [ws.catalog.index]: {
          workspace: wrapperById[ws._id],
          fields: _.keyBy([
            ...(ws?.catalog?.fields ?? [])
            //..._.flatMap(ws.seasonSelectors?.map(s => s.fields) ?? [])
          ], f => f.field)
        }
      }), {})
  }

  @observable.ref workspaces: Array<Workspace> = []

  @computed get workspaceStore() {
    const {workspace, storesByWorkspace} = this
    return storesByWorkspace[workspace?._id]
  }

  @computed get workspace() {
    const {workspaces, current} = this

    return workspaces.find(ws => ws.config._id === current) // ?? _.first(workspaces)
  }

  @computed get availableWorkspaces(): Array<WorkspaceConfig> { return _.orderBy(this.workspaces.map(ws => ws.config), (config: WorkspaceConfig) => _.isFunction(config.visible) ? config.visible(this.catalog.stores) : config.visible, 'desc')}

  @computed get workspaceConfig() { return this.workspace?.config }

  @computed get localStorageKey() {
    const {workspaceConfig} = this, {selectedDeploymentId} = deployments
    if (!selectedDeploymentId || !workspaceConfig) return null
    return `${selectedDeploymentId}-${workspaceConfig._id}`
  }

  @computed get deploymentConfig(): DeploymentConfig { return deployments.deploymentConfig }

  @observable current: WorkspaceId = null

  @computed get currentConfig() {
    return this.workspaceConfig
  }

  @computed get byId(): { [id: string]: WorkspaceConfig } {
    return _.keyBy(this.availableWorkspaces, ws => ws._id)
  }

  @computed get wrapperById(): { [id: string]: Workspace } {
    return _.keyBy(this.workspaces, ws => ws.config._id)
  }

  @computed get pdfConfig(): DeploymentPdfConfig {
    return this.deploymentConfig?.pdf
  }

  @computed get changeLogConfig() : WorkspaceChangeLogConfig {
    const {deploymentConfig} = this
    return deploymentConfig?.changeLog  //?? DEFAULT_CHANGE_LOG_CONFIG // Can't do until servers are actually up to date with correct mappings
  }

  @computed get detailAggregations() {
    return this.workspaceConfig?.catalog?.features?.groupBy?.detailAggregations ?? []
  }

  librariesOfWorkspace = computedFn((catalog: CatalogStore, ws: Partial<WorkspaceConfig>): Array<LibraryItemConfigWrapper> => {
    let r = [
      ...(deployments.deploymentConfig?.libraries ?? []),
      ...(ws?.libraries ?? [])
    ] //.filter(l => l && (!ws?._id || l.name != ws?._id))

    // if (DEBUG) {
    //   const uniq = _.uniqBy(r, r => r.name)
    //
    //   if (DEBUG && r.length != uniq.length) {
    //     console.warn(`Some libraries are being dropped due to conflicting names - `, toJS({r, uniq}))
    //   }
    //   return uniq.map(l => new LibraryItemConfigWrapper(l))
    // }

    return r.map(l => new LibraryItemConfigWrapperStore(catalog, l))
  })

  @computed get librariesOfCurrentWorkspace(): Array<LibraryItemConfigWrapper> {
    return this.librariesOfWorkspace(this.catalog, this.workspaceConfig)
  }

  @computed get visibleLibraries(): Array<LibraryItemConfigWrapper> {
    return this.librariesOfCurrentWorkspace.filter(l => l.config.visible !== false)
  }

  /** We have libraries defined in the left sidebar, but we can also open the bar directly from a group
   */
  @computed get libraryById() { return _.keyBy(this.librariesOfCurrentWorkspace, l => LibraryItemConfigWrapperStore.idFor(this.catalog, l)) ?? {}}

  @computed get libraryByIndex() { return _.keyBy(this.librariesOfCurrentWorkspace, l => l.config.index) ?? {}}

  @computed get view(): 'catalog' | 'library' {
    return this.workspaceConfig?.view ?? 'catalog'
  }

  /**
   * Assumes that each index is used only once as a workspace
   */
  @computed get byIndexScalar(): { [esIndex: string]: WorkspaceConfig } {
    return _.keyBy<WorkspaceConfig>(this.availableWorkspaces.filter(ws => ws?.catalog?.index), ws => ws.catalog.index)
  }

  @computed get wrapperByIndex(): { [esIndex: string]: Workspace } {
    return _.keyBy(this.workspaces, ws => ws.catalog?.index)
  }

  @computed get byIndex(): { [esIndex: string]: Array<WorkspaceConfig> } {
    return _.groupBy(this.availableWorkspaces.filter(ws => ws?.catalog?.index), ws => ws.catalog.index)
  }

  @action getWorkspaceLastSync = async (workspace?: Workspace) => {
    const {es} = globalStores
    const {hits} = await es.search({
      meta: 'Get Workspace Last Sync',
      index: es.indexes.changeHistory,
      body: {
        size: 1,
        sort: [
          {
            timestamp: {
              order: 'desc',
              unmapped_type: 'long'
            }
          }
        ],
        query: {
          bool: {
            filter: [
              {
                term: {'index': workspace?.catalog?.index ?? this.workspaceConfig.catalog.index}
              }, {
                term: {'operation': 'WORKSPACE_SYNC'}
              }
            ]
          }
        }
      }
    }, {log: {query: false}})

    const payload = hits?.hits[0]?._source?.payload
    return payload ? JSON.parse(payload)?.data?.after?.syncTime : ''
  }

  @computed get storesByWorkspace() {
    const {workspaces} = this
    return workspaces.reduce((acc, workspace) => ({...acc, [workspace.config._id]: new StoresInstance(`storesByWorkspace(${workspace.id})`, workspace)}), {})
  }

  storesForWorkspace = (ws: Workspace) => {
    let result = !ws?._id ? null : this.storesByWorkspace[ws._id]

    if (!result && ws?._id) {
      result = {
        [ws._id]: new StoresInstance(`storesForWorkspace(${ws.id})`, ws)
      }
    }

    return result
  }

  workspaceFor = (workspaceId: string) => {
    return this.workspaces.find(ws => ws.id == workspaceId)
  }

  @computed get workspaceByIndex(): { [esIndex: string]: Array<Workspace> } {
    return _.groupBy(this.workspaces, ws => ws.catalog?.index)
  }

  workspaceForLibrary = (l: LibraryItemConfigWrapper): Workspace => {
    const {workspaces} = this
    return workspaces.find(ws => ws.config.catalog?.index === l.index)
  }

  workspaceForImageSource = (is: CatalogImageSourceConfig, catalog: CatalogStore) => {
    if (!is || is.field === 'images') return catalog.workspace

    if (is.index) return this.wrapperByIndex[is.index]

    // debugger
    return catalog.workspace
  }

  labelForIndex = (index: string) => {
    if (!index) return this.catalog.strings.noValue

    const {wrapperByIndex} = this,
      ws = wrapperByIndex[index]

    if (!ws) {
      switch (index) {
        default:
          return index
      }
    }

    return ws.catalog?.catalogItemName ?? ws.title
  }

  workspaceForIndex = (esIndex: string)  : Workspace => {
    return _.first(this.workspaceByIndex[esIndex])
  }

  @computed get defaultCatalogUserSettings(): Partial<ICatalogUserSettings> {
    return this.deploymentConfig?.settings?.defaults?.catalog?.userSettings ?? {}
  }
}




// WEBPACK FOOTER //
// ./src/stores/workspace/WorkspacesStore.ts